import { Dictionary } from "../types/Dictionary";
import { isDefined } from "../types/is-defined";

/**
 * Serialize a dictionary of:
 * - *primitives*
 * - *Array* of primitives
 * - *Record* of primitives. (will not accept nested objects)
 * into encoded query-parameters string.
 *
 *  WARNING: ⚠️  Without the ? at the start.
 *
 * @example
 * ```
 * serializeQueryParams({num: 1234, foo: "bar", flag: false, none: "", arr: [1,2,3]}); // => "num=1234&foo=bar&flag=false&none=&arr[]=1&arr[]=2&arr[]=3"
 * ```
 */
export function serializeQueryParams<P extends QueryParams>(params: Readonly<P>, opts?: Partial<Options>) {
  const fullOpts = opts ? { ...DEFAULT_OPTIONS, ...opts } : DEFAULT_OPTIONS;
  const urlSearchParams = new URLSearchParams();
  Object.entries(params)
    .filter(([, value]) => isDefined(value))
    .forEach(([key, value]: [string, QueryParamValue]) => {
      const isArray = Array.isArray(value);
      const isObject = typeof value === "object" && !isArray;
      if (isArray) {
        value.forEach((el) => {
          urlSearchParams.append(key + fullOpts.arrayPostfix, String(el));
        });
      } else if (isObject) {
        Object.entries(value).forEach(([oKey, oValue]) => {
          urlSearchParams.append(`${key}[${oKey}]`, String(oValue));
        });
      } else {
        urlSearchParams.append(key, String(value));
      }
    });
  return urlSearchParams.toString();
}

type Options = { arrayPostfix: string };
const DEFAULT_OPTIONS: Options = { arrayPostfix: "" };
export type QueryParams = Dictionary<QueryParamValue>;
export type QueryParamValue = Primitive | Array<Primitive> | Dictionary<Primitive>;
export type Primitive = string | number | boolean;
